.gallery-page {
  --content-max: 1500px;
  --container-pad: clamp(16px, 4vw, 48px);
  --section-gap: clamp(32px, 6vw, 120px);
  --image-gap: clamp(8px, 1.5vw, 16px);

  .page {
    width: 100%;
    min-height: 100%;
    height: auto;
    overflow: visible;
    position: relative;
    background-color: rgba(255, 255, 255, 1);
  }

  // ---------------------------------------------------------------------------
  // Hero Section
  // ---------------------------------------------------------------------------
  .gallery-hero {
    position: relative;
    width: 100%;
    min-height: clamp(620px, 78vh, 920px);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    overflow: hidden;
  }

  .gallery-hero__overlay {
    position: absolute;
    inset: 0;
    background-color: rgba(22, 0, 4, 0.45);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .gallery-hero__content {
    width: min(92vw, var(--content-max));
    padding: 0 var(--container-pad);
    box-sizing: border-box;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: clamp(16px, 2.4vw, 28px);
  }

  .gallery-hero__title {
    margin: 0;
    width: min(92vw, 24ch);
    color: rgba(255, 255, 255, 1);
    font-size: clamp(38px, 6.5vw, 120px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    text-align: center;
    text-transform: uppercase;
    line-height: 1.02;
    white-space: normal;
  }

  .gallery-hero__subtitle {
    margin: 0;
    width: min(92vw, 92ch);
    color: rgba(255, 255, 255, 1);
    font-size: clamp(15px, 1.4vw, 18px);
    font-family: Red Rose-Light;
    font-weight: 300;
    text-align: center;
    line-height: 1.75;
  }

  // ---------------------------------------------------------------------------
  // Gallery Main
  // ---------------------------------------------------------------------------
  .gallery-main {
    width: 100%;
    padding: clamp(32px, 5vw, 64px) var(--container-pad);
    box-sizing: border-box;
  }

  // ---------------------------------------------------------------------------
  // Masonry Grid
  // ---------------------------------------------------------------------------
  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(clamp(250px, 30vw, 450px), 1fr));
    gap: var(--image-gap);
    max-width: 100%;
    width: 100%;
    margin: 0 auto;
  }

  .gallery-item {
    position: relative;
    overflow: hidden;
    aspect-ratio: 1;
    cursor: pointer;
    border-radius: 4px;
    transition: transform 0.3s ease-out;

    &:hover {
      transform: scale(1.02);

      .gallery-image {
        transform: scale(1.06) translateY(-6px);
      }

      .gallery-overlay {
        opacity: 1;
      }

      .gallery-overlay__content {
        transform: translateY(0);
        opacity: 1;
      }
    }

    &:focus-visible {
      outline: 2px solid rgba(22, 0, 4, 1);
      outline-offset: -2px;
    }
  }

  .gallery-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease-out;
  }

  .play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;

    svg {
      width: clamp(28px, 5vw, 40px);
      height: clamp(28px, 5vw, 40px);
      filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.15));
    }
  }

  .gallery-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.45));
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition:
      opacity 0.35s ease,
      transform 0.35s ease;
    pointer-events: none;

    &__content,
    .gallery-overlay__content {
      text-align: center;
      color: #f7efe6;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      transform: translateY(6px);
      opacity: 0;
      transition:
        transform 0.35s ease,
        opacity 0.35s ease;
    }

    &__title,
    .gallery-overlay__title {
      margin: 0;
      color: #f7efe6;
      font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
      font-size: clamp(20px, 3.6vw, 40px);
      line-height: 1;
      letter-spacing: 0.02em;
    }

    &__line,
    .gallery-overlay__line {
      width: 48px;
      height: 1px;
      background: rgba(247, 239, 230, 0.95);
    }

    &__cta,
    .gallery-overlay__cta {
      color: rgba(247, 239, 230, 0.95);
      font-family: 'Red Rose-Regular', sans-serif;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 3px;
      font-weight: 600;
    }
  }

  // ---------------------------------------------------------------------------
  // Lightbox
  // ---------------------------------------------------------------------------
  .lightbox {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.95);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: clamp(16px, 4vw, 48px);
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .lightbox__content {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .lightbox__image {
    max-width: 100%;
    max-height: 85vh;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }

  .lightbox__video {
    max-width: 100%;
    max-height: 85vh;
    width: auto;
    height: auto;
    display: block;
    background: #000;
  }

  .lightbox__close {
    position: absolute;
    top: clamp(16px, 3vw, 32px);
    right: clamp(16px, 3vw, 32px);
    background: none;
    border: none;
    color: rgba(255, 255, 255, 1);
    font-size: clamp(24px, 5vw, 36px);
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
    transition: transform 0.2s ease-out;

    &:hover {
      transform: scale(1.2);
    }

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 1);
      outline-offset: 4px;
    }
  }

  .lightbox__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: rgba(255, 255, 255, 1);
    font-size: clamp(28px, 6vw, 48px);
    cursor: pointer;
    padding: clamp(8px, 2vw, 16px) clamp(12px, 3vw, 24px);
    transition: all 0.2s ease-out;
    z-index: 11;
    user-select: none;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-50%) scale(1.1);
    }

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 1);
      outline-offset: 4px;
    }
  }

  .lightbox__nav--prev {
    left: clamp(16px, 3vw, 32px);
  }

  .lightbox__nav--next {
    right: clamp(16px, 3vw, 32px);
  }

  .lightbox__counter {
    position: absolute;
    bottom: clamp(16px, 3vw, 32px);
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.7);
    font-size: clamp(12px, 1.2vw, 16px);
    font-family: 'Red Rose-Regular', serif;
    background: rgba(0, 0, 0, 0.3);
    padding: 8px 16px;
    border-radius: 20px;
    z-index: 12;
  }

  // ---------------------------------------------------------------------------
  // Responsive Breakpoints
  // ---------------------------------------------------------------------------
  @media (max-width: 1023px) {
    .gallery-hero {
      min-height: clamp(580px, 74vh, 760px);
    }

    .gallery-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: var(--image-gap);
    }
  }

  @media (max-width: 767px) {
    .gallery-hero {
      min-height: clamp(540px, 70vh, 680px);
    }

    .gallery-hero__title {
      font-size: clamp(38px, 6vw, 64px);
    }

    .gallery-main {
      padding: clamp(20px, 4vw, 40px) var(--container-pad);
    }

    .gallery-grid {
      grid-template-columns: 1fr;
      gap: var(--image-gap);
    }

    .lightbox__nav {
      font-size: clamp(24px, 5vw, 36px);
      padding: clamp(6px, 1.5vw, 12px) clamp(10px, 2vw, 16px);
    }

    .lightbox__nav--prev {
      left: clamp(8px, 2vw, 16px);
    }

    .lightbox__nav--next {
      right: clamp(8px, 2vw, 16px);
    }

    .lightbox__image {
      max-height: 80vh;
    }

    .lightbox__video {
      max-height: 80vh;
    }
  }
}
